import Button from "antd/es/button"
import type { ColumnsType } from "antd/es/table"
import Table from "antd/es/table"
import type { User } from "firebase/auth"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { useEffect, useState } from "react"

import { EMPTY_ARRAY } from "../../constants"
import { makeConverter } from "../../dbUtils"
import { db } from "../../firebaseApp"
import type { GroupInvite } from "../../groups/types"
import { useAcceptInvite } from "../../hooks/useAcceptInvite"
import { INVITES_COLLECTION } from "../../types/common"
import { toHumanReadableString } from "../../utils"

const MyInvitesManager: React.FC<{ user: User }> = ({ user }) => {
  const [pendingInvites, setPendingInvites] =
    useState<GroupInvite[]>(EMPTY_ARRAY)
  const [loading, setLoading] = useState<boolean>(true)
  const { loading: isLoadingAccept, handleAcceptInvite } = useAcceptInvite()

  useEffect(() => {
    const allDocsQuery = query(
      collection(db, INVITES_COLLECTION),
      where("invitee_email", "==", user.email),
    ).withConverter(makeConverter<GroupInvite>())

    return onSnapshot(allDocsQuery, (querySnapshot) => {
      setLoading(false)
      for (const change of querySnapshot.docChanges()) {
        setPendingInvites((prevDocs) => {
          const prevWithoutChange = prevDocs.filter(
            (d) => d.oid !== change.doc.id,
          )
          const prevWithChange = [change.doc.data(), ...prevWithoutChange]
          return prevWithChange.filter((d) => d.status === "PENDING")
        })
      }
    })
  }, [user.email])

  if (pendingInvites.length === 0) {
    return null
  }

  const columns: ColumnsType<GroupInvite> = [
    {
      title: "Group Name",
      dataIndex: "group_name",
      key: "group_name",
      sorter: (a: GroupInvite, b: GroupInvite) =>
        a.group_name.localeCompare(b.group_name),
    },
    {
      title: "Inviter",
      dataIndex: "creator_email",
      key: "creator_email",
      sorter: (a: GroupInvite, b: GroupInvite) =>
        a.creator_email.localeCompare(b.creator_email),
    },
    {
      title: "Date Invited",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: GroupInvite, b: GroupInvite) =>
        a.created_at.toMillis() - b.created_at.toMillis(),
      render: (created_at: GroupInvite["created_at"]) =>
        toHumanReadableString(created_at),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record: GroupInvite) => (
        <Button
          type="primary"
          onClick={() => handleAcceptInvite(record.oid)}
          loading={isLoadingAccept}
          disabled={isLoadingAccept}
        >
          Accept
        </Button>
      ),
    },
  ]

  return (
    <section>
      <div className="mb-8">
        <h3>Invitations Received</h3>
        <Table
          dataSource={pendingInvites}
          columns={columns}
          rowKey="oid"
          loading={loading}
          pagination={false}
        />
      </div>
    </section>
  )
}

export default MyInvitesManager
